import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card, Chip, ArrowLink } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../sections/CTASection";
import { PILLARS } from "../data/capabilities";

/**
 * /consulting
 *
 * The hardest thing to make credible on a consulting page is that the
 * advice is worth paying for, and the usual move — listing eight practice
 * areas — actively works against it. So the page leads with what lands on
 * the customer's desk at the end, and states plainly that "do not build
 * this" is one of the possible answers. A consultancy that can say that is
 * a different proposition from one that cannot.
 */

const DELIVERABLES = [
  {
    title: "A recommendation, with the reasoning attached",
    body: "What to build, what to buy, what to leave alone — and the tradeoffs behind each, written down so your team can argue with it after we leave.",
  },
  {
    title: "A reference architecture",
    body: "Services, data model, integration points, where the AI sits and where it does not. Concrete enough that an engineering team can start against it on Monday.",
  },
  {
    title: "A costed roadmap",
    body: "Sequenced phases with what each one costs to build and to run. Cloud and model costs projected at your actual volumes, not at demo volumes.",
  },
  {
    title: "An AI feasibility read",
    body: "Whether a model genuinely beats conventional code for your problem, what accuracy is achievable on your data, and what it takes to evaluate it honestly.",
  },
];

const SCENARIOS = [
  {
    when: "You have an AI mandate and no obvious place to spend it",
    then: "We find the two or three processes where a model actually creates leverage, and say which of the rest are better served by ordinary software.",
  },
  {
    when: "You are choosing between building and buying",
    then: "We cost both honestly, including the operating burden of the thing you build — which is the number that is usually missing.",
  },
  {
    when: "Your platform is holding back the growth target",
    then: "We find the specific constraint — data model, tenancy, a synchronous path that should not be — and sequence the work to remove it without a rewrite.",
  },
  {
    when: "The pilot worked and production is not going well",
    then: "Demos and production fail differently. We identify what the demo did not have to survive: load, bad input, edge cases, cost at volume, and the people operating it.",
  },
];

export function Consulting() {
  const pillar = PILLARS[0];

  return (
    <>
      <PageHero
        eyebrow="AI & IT Consulting"
        title={
          <>
            Decide what to build{" "}
            <span className="text-gradient">before you build it.</span>
          </>
        }
        lede="Most expensive technology mistakes are made in the first three weeks, on a whiteboard, by people who have not yet had to operate the result. We have — which is the only reason our advice is worth anything."
        primary={{ href: "/contact", label: "Talk to an expert" }}
        secondary={{ href: "/software-development", label: "See how we build" }}
        aside={
          <Card className="p-6 sm:p-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
              What we cover
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {pillar.capabilities.map((c) => (
                <Chip key={c}>{c}</Chip>
              ))}
            </div>
            <div className="mt-6 border-t border-white/[0.08] pt-5">
              <p className="text-sm leading-relaxed text-white/60">
                “Do not build this” is a legitimate outcome of an engagement,
                and we have delivered it. It is cheaper for you than the
                alternative and better for us than a project nobody wanted.
              </p>
            </div>
          </Card>
        }
      />

      <Section id="deliverables">
        <div className="container">
          <SectionHead
            eyebrow="What you get"
            title={
              <>
                Advice you can hand to an{" "}
                <span className="text-gradient">engineering team.</span>
              </>
            }
            lede="Every engagement ends in artefacts, not a slide deck and a handshake. If your team cannot start building from what we leave behind, we have not finished."
          />

          <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-2">
            {DELIVERABLES.map((d, i) => (
              <Reveal key={d.title} delay={(i % 2) * 0.07}>
                <Card interactive className="h-full p-6 sm:p-7">
                  <div className="font-mono text-[11px] tabular-nums text-white/25">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold tracking-tight">
                    {d.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">
                    {d.body}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="scenarios" tight>
        <div className="container">
          <SectionHead
            eyebrow="Where we are most useful"
            title={
              <>
                Four situations we{" "}
                <span className="text-gradient">recognise immediately.</span>
              </>
            }
          />

          <div className="mt-10 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
            {SCENARIOS.map((s, i) => (
              <Reveal
                key={s.when}
                delay={i * 0.05}
                className={i > 0 ? "border-t border-white/[0.08]" : ""}
              >
                <div className="grid gap-3 p-6 transition-colors hover:bg-white/[0.03] sm:grid-cols-[0.9fr_1.1fr] sm:gap-8 sm:p-7">
                  <div className="flex gap-3">
                    <span className="mt-1.5 h-px w-6 shrink-0 bg-emerald-400/60" />
                    <h3 className="font-display text-base font-semibold leading-snug tracking-tight text-white">
                      {s.when}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-white/62">{s.then}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-6">
              <ArrowLink href="/what-we-do">
                See how consulting connects to the rest of what we do
              </ArrowLink>
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection
        title={
          <>
            Not sure what you need{" "}
            <span className="text-gradient">yet?</span>
          </>
        }
        lede="That is the normal state, and it is a good reason to talk rather than a reason to wait. Describe the problem in your own words — we will tell you whether it is a strategy question, an engineering question, or something you already own the answer to."
        primary={{ href: "/contact", label: "Talk to an expert" }}
        secondary={{ href: "/work", label: "See what we have built" }}
      />
    </>
  );
}
